import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnFilter, type RowSelectionState, type Updater } from "@tanstack/react-table";
import { TableErrorWrapper } from "../../components/table/TableErrorWrapper";
import {
  type SearchTermPerformanceV4Row,
  useGetV4SearchTermPerformanceByQuery,
} from "./api/v4SearchTermPerformanceReportByQuery";
import { type ManagedTableState, type ManagedTableStateSetters } from "../../components/table/usePersistedTableState";
import { useDebounce } from "@reactuses/core";
import { useBlockWindowQuery } from "../../components/table/useBlockWindowQuery";
import { HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { Button } from "../../components/core/basic/Button";
import { resetTable } from "../../components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../components/fields/base/TextField";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../dataViews/components/DataViewPicker";
import { css } from "../../../styled-system/css";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";
import QueryExpander from "../query_funnel/QueryExpander";
import { SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS } from "../advertising/filters/targetLifecycleFilters";
import { DownloadV4Report } from "../perf_reports/DownloadReport";
import { useSimpleCan } from "../../config/casl/casl";
import { useV4SearchTermReportExport } from "./api/v4SearchTermPerformanceReportExport";
import { BulkActionButton } from "../../components/table/BulkActionButton";
import { useAddTargetsToAd } from "../advertising/api/useAddTargets";
import {
  AddTargetsModal,
  type InitialFormValues,
} from "../../components/custom/category/advertisements/AddTargetsWidget";
import { GOALS } from "../../components/core/blocks/AdPlan/constants";
import { extractAsin } from "../../utils/extractAsin";
import QueryNegationMatchTypeSelector from "./components/QueryNegationMatchTypeSelector";
import { QueryMatchExpander } from "./components/QueryMatchExpander";
import { DIMENSION_TYPES, SEARCH_TERM_DIMENSIONS } from "./utils/reportingConstants";
import { useServerTable, type AppRow, type AppColumns } from "../../components/table/tableFeatures";

export type SelectedRowData = {
  query: string;
  keyword_text: string;
  advertisement_id: number;
  ad_group_id?: number;
};

const getStableRowId = (row: SearchTermPerformanceV4Row, dimensions: string) => {
  if (dimensions === SEARCH_TERM_DIMENSIONS.QUERY) {
    return row.query;
  }
  return [
    row.query,
    row.keyword_text,
    row.match_type,
    row.campaign_id,
    row.adv_keyword_ids[0] ?? "",
    row.adv_target_ids[0] ?? "",
  ].join("||");
};

function SearchTermReportDashboardTable({
  columns,
  state,
  setters,
  initialState,
  dimensions,
  renderExpander = true,
  categoryFilterOptions = [],
  additionalFilters,
  parentFilters = [],
  parentFilterControls = null,
  showBulkActionsButton = false,
  enableRowSelection = true,
  isAdPlanDetailsTable = false,
  renderDownloadButton = true,
}: {
  columns: AppColumns<SearchTermPerformanceV4Row>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  dimensions: string;
  renderExpander?: boolean;
  additionalFilters?: { campaignId?: string };
  categoryFilterOptions?: { value: number | string; label: string }[];
  targetType: string | null;
  parentFilters?: ColumnFilter[];
  parentFilterControls?: React.ReactNode;
  showBulkActionsButton?: boolean;
  enableRowSelection?: boolean;
  isAdPlanDetailsTable?: boolean;
  renderDownloadButton?: boolean;
}) {
  const { t } = useTranslation(["str", "common"]);
  const rowSelection = state.rowSelection;
  const setRowSelection = setters.onRowSelectionChange;
  const [selectedRowsData, setSelectedRowsData] = useState<Record<string, SelectedRowData>>({});
  const [selectedMatchTypes, setSelectedMatchTypes] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkInitialData, setBulkInitialData] = useState<InitialFormValues>();

  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportAds = useSimpleCan("export", "Ad");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const isFlattenedSearchTermReport =
    dimensions.includes(DIMENSION_TYPES.KEYWORD_ID) || dimensions.includes(DIMENSION_TYPES.TARGET_ID);

  const byQueryGetter = useGetV4SearchTermPerformanceByQuery(dimensions);

  const filters = useMemo(() => {
    const campaignId = additionalFilters?.campaignId;
    const campaignFilter = campaignId ? [{ id: "campaign_id", value: { value: campaignId, op: "=" } }] : [];
    return [...parentFilters, ...campaignFilter, ...state.columnFilters];
  }, [additionalFilters, parentFilters, state.columnFilters]);

  const queryResult = useBlockWindowQuery(byQueryGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters,
    search: debounceSearch,
  });

  const addTargetsMutation = useAddTargetsToAd();

  const unmanagedAdIds = useMemo(() => {
    const rows: SearchTermPerformanceV4Row[] = queryResult.data?.data ?? [];
    return Array.from(
      new Set(
        rows.filter((row) => row.ad_goal === GOALS.IMPORTED_ADS.value && row.managed === "No").map((row) => row.ad_id),
      ),
    );
  }, [queryResult.data?.data]);

  const negateTargets = useCallback(() => {
    const rows = Object.values(selectedRowsData);

    rows.forEach((row) => {
      let keywordText = row.query;
      const isAsin = !!extractAsin(keywordText);

      if (isAsin) {
        const asin = extractAsin(keywordText);
        keywordText = asin ?? keywordText;
      }

      const body = {
        data: {
          advertisement: row.advertisement_id,
          ad_group: String(row.ad_group_id),
          target_type: isAsin ? "product" : "keyword",
          channel: "amazon",
          action: "create_targets" as const,
          targets:
            isAsin ?
              {
                products: [keywordText],
                targetTypes: ["negative_product"],
              }
            : [
                {
                  text: keywordText,
                  matchTypes: selectedMatchTypes,
                },
              ],
        },
      };
      addTargetsMutation.mutate(body);
    });
  }, [selectedRowsData, selectedMatchTypes, addTargetsMutation]);

  const selected = useMemo(() => Object.keys(selectedRowsData), [selectedRowsData]);

  const bulkActionOptions = useMemo(() => {
    if (dimensions === SEARCH_TERM_DIMENSIONS.QUERY) {
      return [
        {
          label:
            selected.length === 1 ?
              t(($) => $.str["Add Search Query to Media Plans"])
            : t(($) => $.str["Add Search Queries to Media Plans"]),
          action: () => {
            const selectedQueries = [
              ...new Set(
                Object.values(selectedRowsData)
                  .map((row) => row.query)
                  .filter((q) => !extractAsin(q)),
              ),
            ];
            setBulkInitialData({
              targets: selectedQueries.map((q) => ({
                text: q,
                matchTypes: ["exact", "phrase", "broad"],
              })),
            });
            setShowAddModal(true);
          },
          confirmationMessage: null,
        },
      ];
    }

    if (isFlattenedSearchTermReport || isAdPlanDetailsTable) {
      return [
        {
          label: t(($) => $.str["Negate Query Text"]),
          action: () => {
            negateTargets();
          },
          actionFormFields: (
            <QueryNegationMatchTypeSelector
              selectedMatchTypes={selectedMatchTypes}
              setSelectedMatchTypes={setSelectedMatchTypes}
              selectedRowsData={selectedRowsData}
            />
          ),
          disableConfirmButton: dimensions.includes(DIMENSION_TYPES.KEYWORD_ID) && selectedMatchTypes.length === 0,
          confirmationMessage: (
            <p className={css({ padding: "medium", fontSize: "medium" })}>
              {t(($) => $.str["Are you sure you want to negate the selected search queries?"])}
            </p>
          ),
        },
      ];
    }

    return [];
  }, [
    dimensions,
    isFlattenedSearchTermReport,
    negateTargets,
    selected.length,
    selectedMatchTypes,
    selectedRowsData,
    isAdPlanDetailsTable,
    t,
  ]);

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const newRowSelection = typeof updater === "function" ? updater(rowSelection) : updater;

    if (Object.keys(newRowSelection).length === 0) {
      setRowSelection({});
      setSelectedRowsData({});
      return;
    }

    const updatedSelectedRowsData = { ...selectedRowsData };

    for (const row of table.getRowModel().rows) {
      const id = row.id;

      if (newRowSelection[id]) {
        updatedSelectedRowsData[id] = {
          query: row.original.query,
          keyword_text: row.original.keyword_text,
          advertisement_id: row.original.ad_id,
          ad_group_id: row.original.ad_group_id,
        };
      } else {
        // oxlint-disable-next-line typescript/no-dynamic-delete
        delete updatedSelectedRowsData[id];
      }
    }

    setRowSelection(newRowSelection);
    setSelectedRowsData(updatedSelectedRowsData);
  };

  const table = useServerTable({
    data: queryResult.data?.data,
    columns,
    getRowCanExpand: () => renderExpander,
    getRowId: (row) => {
      const rowId = getStableRowId(row, dimensions);
      return rowId;
    },
    enableRowSelection: (row) => {
      const isQueryDimension = dimensions === SEARCH_TERM_DIMENSIONS.QUERY;
      return (
        (!["keyword_text,match_type", "keyword_text"].includes(dimensions) &&
          (isQueryDimension || !unmanagedAdIds.includes(row.original.ad_id)) &&
          enableRowSelection) ||
        isAdPlanDetailsTable
      );
    },
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      ...state,
      rowSelection,
      expanded: state.expanded,
    },
    ...Object.fromEntries(Object.entries(setters).filter(([key]) => key !== "onRowSelectionChange")),
    rowCount: queryResult.data?.count ?? 0,
    enableColumnPinning: true,
    initialState,
    alternateSortingEnabled: true,
    meta: {
      showSkeleton: queryResult.isFetching && (!queryResult.data || queryResult.isPlaceholderData),
      columnFilterSelectOptions: {
        match_type: SEARCH_TERM_REPORT_MATCH_TYPE_OPTIONS,
        category_id: categoryFilterOptions,
      },
    },
  });

  const exportMutation = useV4SearchTermReportExport(
    isAdPlanDetailsTable ? SEARCH_TERM_DIMENSIONS.QUERY_MATCH_KEYWORD : dimensions,
    filters,
  );

  return (
    <>
      {showAddModal && (
        <AddTargetsModal
          isOpen={showAddModal}
          onOpenChange={setShowAddModal}
          targetType="keyword"
          renderAdSelector
          initialData={bulkInitialData}
          hideNegativeMatchTypeOptions={true}
        />
      )}
      <div>
        {showBulkActionsButton && (
          <Wrap
            className={css({
              justifyContent: "flex-end",
              width: "100%",
              maxWidth: "88vw",
              padding: "small",
              fontSize: "large",
            })}
          >
            <BulkActionButton options={bulkActionOptions} selected={selected} isDisabled={!userCanUpdateAds} />
          </Wrap>
        )}
        <HStack maxWidth={"88vw"} padding="small" fontSize="large">
          <VStack flex="1" alignItems="start">
            <HStack width="100%">
              <Button variant="contentOnly" onPress={() => resetTable(table, "searchTermReport")} fontSize="xxlarge">
                <MdRotateLeft />
              </Button>
              <RT8ColumnSelector table={table} />
              <DataViewPicker viewKey="searchTermReport" datasetKey="str" table={table} />
              <SearchInput
                aria-label={t(($) => $.common.actions["Search"])}
                isExpandable
                css={{ flex: "0 1 350px" }}
                value={table.state.globalFilter}
                onChange={table.setGlobalFilter}
              />
              {parentFilterControls}
            </HStack>
            <RT8FilterWidget table={table} />
          </VStack>
          <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
        </HStack>
        <TableErrorWrapper error={queryResult.error} userMessage={t(($) => $.str["Error loading stats"])}>
          <Table
            table={table}
            className={css({
              width: "max-content",
            })}
            renderExpander={
              renderExpander ?
                (row: AppRow<SearchTermPerformanceV4Row>) =>
                  dimensions === SEARCH_TERM_DIMENSIONS.QUERY ?
                    <QueryMatchExpander
                      row={row}
                      categoryFilterOptions={categoryFilterOptions}
                      parentFilters={parentFilters}
                    />
                  : <QueryExpander
                      row={row}
                      dims={dimensions}
                      isFlattenedSearchTermReport={isFlattenedSearchTermReport}
                      query={row.original.query}
                      keyword_text={row.original.keyword_text}
                      match_type={row.original.match_type}
                      marketPlace={row.original.marketplace}
                      context={`search-term-report-${dimensions}`}
                      renderNegativeKeywordsTab={false}
                      useClickhouseSearchTermReportData={true}
                    />
              : undefined
            }
            enableSelectAll={
              ![SEARCH_TERM_DIMENSIONS.KEYWORD_TEXT_WITH_MATCH, SEARCH_TERM_DIMENSIONS.KEYWORD_TEXT].includes(
                dimensions,
              ) && enableRowSelection
            }
          />
        </TableErrorWrapper>
        {userCanExportAds && renderDownloadButton && (
          <HStack width="100%" padding="small">
            <DownloadV4Report downloadMutation={exportMutation} />
          </HStack>
        )}
      </div>
    </>
  );
}

export default SearchTermReportDashboardTable;
